import type { Permission } from '@/api/generated/enums';
import { blockedReason } from '@core/session/capability';
import type { Session } from '@core/session/types';

/**
 * Giving a member of staff a way to sign in — the rules, kept away from the drawer's markup.
 *
 * <p>
 * <strong>Two permissions, not one, and that is the whole security argument.</strong> Minting an
 * account is an identity act: it takes a seat on the plan, it opens its own audit trail, and it
 * hands somebody a password. A person holding only <c>Employee:Edit</c> could otherwise create a
 * sign-in through an HR form, which is privilege escalation wearing a payroll screen. The server
 * carries both attributes; this is the client half, and it exists so the screen never offers a
 * write the server will refuse.
 * </p>
 *
 * <p>
 * <strong>Detaching needs only <c>Employee:Edit</c></strong>, because it creates nothing and
 * revokes nothing — the account stays live and able to sign in. Leaving is an HR event; ending
 * somebody's access is a security event on the Users screen. Requiring <c>User:Create</c> to
 * correct a mislinked row would be asking for the wrong grant.
 * </p>
 */

/** Attach an account they already hold, or mint one. Never both, and never neither. */
export type LoginChoice =
  { kind: 'existing'; userId: number | null } | { kind: 'new'; username: string };

export interface LoginProblem {
  /** Which control answers it, so the drawer can print it beside that field. */
  field: 'user' | 'username';
  message: string;
}

/**
 * Whether this person may hand out a login at all.
 *
 * Returns the sentence to show, or undefined when they may. Both grants are checked, and the
 * *first* missing one is named — a person told they lack `User:Create` after fixing `Employee:Edit`
 * has been sent round the houses once for no reason.
 */
export function linkBlockedReason(session: Session): string | undefined {
  const required: Permission[] = ['Employee:Edit', 'User:Create'];

  for (const permission of required) {
    const reason = blockedReason(session, permission);

    if (reason) {
      return reason;
    }
  }

  return undefined;
}

/** Detaching is an edit of the staff record and nothing more. */
export function unlinkBlockedReason(session: Session): string | undefined {
  return blockedReason(session, 'Employee:Edit');
}

/**
 * A username this product will accept.
 *
 * <p>
 * <strong>It may not begin with a digit, and that is not a style rule.</strong> One sign-in box
 * takes a username *or* a mobile number, which is only unambiguous because no username can look
 * like the start of a number. `LoginIdentifier` is the server's definition; this mirrors it so the
 * drawer refuses before the round trip rather than after it.
 * </p>
 *
 * Trimmed, because a trailing space is invisible in a field and fatal at a counter six weeks later.
 */
export function usernameProblem(raw: string): string | undefined {
  const username = raw.trim();

  if (username.length === 0) {
    return 'Give them a username to sign in with.';
  }

  if (/^\d/.test(username)) {
    return 'A username cannot start with a number — the sign-in box would read it as a mobile.';
  }

  if (username.length < 3) {
    return 'Too short to be a username.';
  }

  return undefined;
}

/**
 * What is wrong with the choice as it stands, or null when it is ready to send.
 *
 * One function rather than a check per control, because the two modes are alternatives: the
 * question "is this ready" cannot be answered by looking at either field alone.
 */
export function loginProblem(choice: LoginChoice): LoginProblem | null {
  if (choice.kind === 'existing') {
    return choice.userId === null
      ? { field: 'user', message: 'Choose the account this person already signs in with.' }
      : null;
  }

  const problem = usernameProblem(choice.username);

  return problem === undefined ? null : { field: 'username', message: problem };
}

/** The request body, which the server reads as "exactly one of these two". */
export interface LinkEmployeeUserRequest {
  userId?: number;
  newUser?: { username: string };
}

/**
 * Builds the body, and refuses to build a bad one.
 *
 * <p>
 * <strong>Only the chosen half is sent.</strong> Passing `userId: null` alongside a `newUser` would
 * be a payload naming both, and the server's reading of "exactly one" is then decided by whichever
 * check it happens to run first — which is a rule nobody wrote down. The key is absent, not null.
 * </p>
 */
export function loginRequest(choice: LoginChoice): LinkEmployeeUserRequest {
  if (loginProblem(choice) !== null) {
    throw new Error('loginRequest called on a choice that is not ready');
  }

  return choice.kind === 'existing'
    ? { userId: choice.userId as number }
    : { newUser: { username: choice.username.trim() } };
}
